import React, { memo } from "react";
import Chip from "@mui/material/Chip";
import Tooltip from "@mui/material/Tooltip";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import classes from "./MultiSelectChipGroup.module.scss";

// 発注検索フィルタの「状態」「日付種類」などで使う汎用の複数選択チップ群。
//
// 契約:
//   value            … 選択中の option.id 配列。呼び出し側(親)が常に1つ以上を保証する
//   onChange(nextIds) … 空配列で呼んではいけない
// 個別チップのトグル結果が空配列になる場合はここで全選択に戻して onChange する
// (「空選択」という状態そのものをこのコンポーネント内で作らない仕様)。
const isAllSelected = (value, allIds) =>
  allIds.length > 0 && allIds.every((id) => value.includes(id));

const MultiSelectChipGroup = memo(
  // help … 任意の補足説明("\n" 区切り可)。指定すると ラベル右の「?」アイコンの
  //        ホバー/フォーカスで MUI Tooltip 表示(ブラウザ標準 title は表示が遅く
  //        見た目も揃わないため使わない)
  ({ label, help, options = [], value = [], onChange, disabled = false }) => {
    const allIds = options.map((option) => option.id);
    const allSelected = isAllSelected(value, allIds);

    // 「すべて」は常に全選択を指示する(現在の状態に関わらずトグルしない)
    const handleSelectAll = () => {
      if (disabled || allIds.length === 0) return;
      onChange(allIds);
    };

    // 個別チップはトグルするが、結果が空になる場合だけ全選択に戻す
    const handleToggle = (id) => {
      if (disabled) return;
      const nextIds = value.includes(id)
        ? value.filter((selectedId) => selectedId !== id)
        : [...value, id];
      onChange(nextIds.length > 0 ? nextIds : allIds);
    };

    return (
      <div className={classes.root}>
        {label && (
          <div className={classes.label}>
            {label}
            {help && (
              <Tooltip
                arrow
                enterTouchDelay={0}
                title={<span className={classes.helpText}>{help}</span>}
              >
                <HelpOutlineIcon
                  className={classes.helpIcon}
                  fontSize="inherit"
                  tabIndex={0}
                  aria-label={`${label}の説明`}
                />
              </Tooltip>
            )}
          </div>
        )}
        <div className={classes.chips}>
          <Chip
            label="すべて"
            size="small"
            clickable
            disabled={disabled}
            variant={allSelected ? "filled" : "outlined"}
            onClick={handleSelectAll}
          />
          {options.map((option) => {
            const selected = value.includes(option.id);
            return (
              <Chip
                key={option.id}
                label={option.label}
                size="small"
                clickable
                disabled={disabled}
                variant={selected ? "filled" : "outlined"}
                // option.color が無い選択中チップは theme の primary 系にフォールバック
                color={selected && !option.color ? "primary" : undefined}
                onClick={() => handleToggle(option.id)}
                sx={
                  selected && option.color
                    ? {
                        backgroundColor: option.color,
                        ...(option.textColor && { color: option.textColor }),
                      }
                    : undefined
                }
              />
            );
          })}
        </div>
      </div>
    );
  }
);

export default MultiSelectChipGroup;
